import { useState } from 'react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useApprovals } from '@/hooks/use-approvals'
import type { ApprovalRequest, ApprovalStatus } from '@riverpark/types'
import { DecideApprovalModal } from './decide-approval-modal'

const STATUS_TABS: { value: ApprovalStatus | undefined; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'failed', label: 'Failed' },
  { value: undefined, label: 'All' },
]

function statusBadgeVariant(status: ApprovalStatus): 'pending' | 'paid' | 'overdue' {
  if (status === 'pending') return 'pending'
  if (status === 'approved') return 'paid'
  return 'overdue' // rejected or failed
}

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function ApprovalsQueuePage() {
  const [status, setStatus] = useState<ApprovalStatus | undefined>('pending')
  const { data, isLoading } = useApprovals(status)
  const [target, setTarget] = useState<ApprovalRequest | null>(null)

  const rows = data?.data ?? []

  return (
    <PageShell>
      <PageHeader
        title="Approvals"
        label="Maker-checker"
        description="Requests the estate manager needs your sign-off on before they take effect."
      />

      <div className="flex gap-1 border-b border-border-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            onClick={() => setStatus(tab.value)}
            className={
              status === tab.value
                ? 'border-b-2 border-primary px-4 py-2.5 font-sans text-sm font-medium text-ink'
                : 'px-4 py-2.5 font-sans text-sm font-medium text-muted-foreground transition-colors hover:text-ink'
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-8 text-center font-sans text-sm text-muted-foreground">
          Nothing here.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded border border-border-2 bg-card px-4 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant={statusBadgeVariant(r.status)}>{r.status}</Badge>
                  <p className="font-sans text-sm font-medium text-ink">{r.summary}</p>
                </div>
                <p className="mt-1 font-sans text-xs text-muted-foreground">
                  Requested by {r.initiatedByName} · {formatDateTime(r.createdAt)}
                  {r.reviewedByName && (
                    <>
                      {' '}
                      · {r.status === 'rejected' ? 'Rejected' : 'Decided'} by {r.reviewedByName}
                    </>
                  )}
                </p>
                {r.reviewNote && (
                  <p className="mt-1 font-sans text-xs text-muted-foreground">
                    Note: {r.reviewNote}
                  </p>
                )}
              </div>
              {r.status === 'pending' && (
                <Button variant="secondary" size="sm" onClick={() => setTarget(r)}>
                  Review
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {target && <DecideApprovalModal request={target} onClose={() => setTarget(null)} />}
    </PageShell>
  )
}
